import React, { useContext, useMemo } from "react";
import { useEffect } from "react";
import IncidentViewer from "./IncedentViewer";
import { getFootage, ReviewStatus } from "./ApiClient";
import { ModalContext } from "./ModalContext";
import { PreviewMap } from "./PreviewMap";
// import { PreviewMap } from "./PreviewMap.tsx.bak";

/**
 * Case footage viewer (sidebar filters + map + incident cards).
 *
 * Data loading
 * - Fetches twice: an "inner" radius (`radiusActive`, default 0.25 mi) and an "outer"
 *   radius (`radiusMiles`). Results are merged; cameras from the inner set default
 *   selected, outer-only cameras default unselected.
 *
 * Camera selection memory (page-lifetime only)
 * - Checkbox picks are kept in a module-level Map keyed by `caseId` so they survive
 *   React remounts / setting changes (hours interval, GPS, radii) without writing to
 *   localStorage. A full page reload resets selections to defaults.
 * - Stored as: unchecked camera IDs + checked outer-only camera IDs (inner defaults on).
 *
 * Sidebar camera list
 * - Split into "Within X mi" (inner) and "Outer (X–Y mi)" sections, each sorted by
 *   distance. Labels show `distance_meters` converted to miles (#.## mi).
 *
 * Status filters still use localStorage (case-scoped keys) and intentionally persist
 * across reloads.
 */

/** Per-case overrides applied when footage is reloaded during the same page visit. */
type CameraSelectionMemory = {
    /** Cameras the user explicitly unchecked (inner or outer). */
    uncheckedCameraIds: number[];
    /** Outer-only cameras the user explicitly checked (inner defaults selected). */
    checkedOuterCameraIds: number[];
};

/** Survives component remounts; cleared automatically on full page reload. */
const cameraSelectionByCase = new Map<string, CameraSelectionMemory>();

function cameraSelectionKey(caseId: number | string | null | undefined) {
    return caseId != null ? `case:${caseId}` : "case:none";
}

function getCameraSelectionMemory(caseId: number | string | null | undefined): CameraSelectionMemory {
    return cameraSelectionByCase.get(cameraSelectionKey(caseId))
        ?? { uncheckedCameraIds: [], checkedOuterCameraIds: [] };
}

function setCameraSelectionMemory(
    caseId: number | string | null | undefined,
    memory: CameraSelectionMemory
) {
    cameraSelectionByCase.set(cameraSelectionKey(caseId), memory);
}

export default function FootageViewer(props) {
    const { lat, lng, datetimeLocal, timezone, onChange, playbackRate, radiusMiles, hoursInterval, caseId, radiusActive } = props;
    const { modalState } = useContext(ModalContext);

    // Status-filter localStorage keys are case-scoped (unlike camera selection memory).
    const scopedKey = (name) => (caseId != null ? `${name}:case:${caseId}` : name);
    // Camera IDs returned by the inner-radius query; drives defaults and sidebar grouping.
    const [innerCameraIds, setInnerCameraIds] = React.useState<Set<number>>(new Set());

    useEffect(() => {
        if (lat && lng) {
            setIsLoading(true);

            const { uncheckedCameraIds, checkedOuterCameraIds } = getCameraSelectionMemory(caseId);

            // Parallel fetch: tight "active" radius + full search radius, then merge.
            Promise.all([
                getFootage(lat, lng, datetimeLocal, timezone, radiusActive, hoursInterval),
                getFootage(lat, lng, datetimeLocal, timezone, radiusMiles, hoursInterval),
            ]).then(([innerData, outerData]) => {
                const nextInnerCameraIds = new Set(innerData.cameras.map(c => c.camera_id));
                setInnerCameraIds(nextInnerCameraIds);

                // Defaults: inner selected, outer-only unselected.
                // Page-memory overrides apply when present (see CameraSelectionMemory).
                const mergedCamerasMap = new Map();

                outerData.cameras.forEach((c) => {
                    const isInner = nextInnerCameraIds.has(c.camera_id);
                    mergedCamerasMap.set(c.camera_id, {
                        ...c,
                        isSelected: uncheckedCameraIds.includes(c.camera_id)
                            ? false
                            : isInner || checkedOuterCameraIds.includes(c.camera_id),
                    });
                });

                innerData.cameras.forEach((c) => {
                    mergedCamerasMap.set(c.camera_id, {
                        ...mergedCamerasMap.get(c.camera_id),
                        ...c,
                        isSelected: !uncheckedCameraIds.includes(c.camera_id),
                    });
                });

                const processFootage = (footageList) => footageList.map((f) => {
                    if (f.metadata) {
                        f.fps = f.metadata.find((m) => m.key === 'fps')?.value;
                        f.refresh = f.metadata.find((m) => m.key === 'refresh')?.value;
                        f.start_date_local = f.metadata.find((m) => m.key === 'start_date_local')?.value;
                        f.end_date_local = f.metadata.find((m) => m.key === 'end_date_local')?.value;
                        f.when = f.metadata.find((m) => m.key === 'when')?.value;
                        f.duration = f.metadata.find((m) => m.key === 'duration')?.value;
                        f.width = f.metadata.find((m) => m.key === 'width')?.value;
                        f.height = f.metadata.find((m) => m.key === 'height')?.value;
                    }
                    return f;
                });

                const mergedFootageMap = new Map();
                processFootage(outerData.footage).forEach(f => mergedFootageMap.set(f.footage_id, f));
                processFootage(innerData.footage).forEach(f => mergedFootageMap.set(f.footage_id, f));

                setCameras([...mergedCamerasMap.values()]);
                setFootage([...mergedFootageMap.values()]);
                setIsLoading(false);
            });
        }

        return () => {};
    }, [lat, lng, datetimeLocal, timezone, radiusActive, radiusMiles, hoursInterval, modalState, caseId]);
    
    useEffect(() => {
        const storedStatusFilters = JSON.parse(localStorage.getItem(scopedKey("statusFilters")) || "{}");

        if (typeof storedStatusFilters.showNotReviewed === 'boolean') {
            setShowNotReviewed(storedStatusFilters.showNotReviewed);
        }
        if (typeof storedStatusFilters.showIncidents === 'boolean') {
            setShowIncidents(storedStatusFilters.showIncidents);
        }
        if (typeof storedStatusFilters.showNoIncidents === 'boolean') {
            setShowNoIncidents(storedStatusFilters.showNoIncidents);
        }
    }, [caseId]);

    const [cameras, setCameras] = React.useState([]);
    const [footage, setFootage] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [showNotReviewed, setShowNotReviewed] = React.useState(true);
    const [showIncidents, setShowIncidents] = React.useState(true);
    const [showNoIncidents, setShowNoIncidents] = React.useState(true);
    
    useEffect(() => {
        if (!isLoading && footage.length > 0 && onChange) {
            onChange(footage);
        }
    }, [isLoading, footage, onChange]);
    
    footage?.forEach((f) => {
        f.camera = cameras?.find((c) => c.camera_id === f.camera_id);
    });

    function updateStatusFilters(updates: Partial<{
        showNotReviewed: boolean,
        showIncidents: boolean,
        showNoIncidents: boolean
    }>) {
        const current = JSON.parse(localStorage.getItem(scopedKey("statusFilters")) || "{}");
        const updated = { ...current, ...updates };
        localStorage.setItem(scopedKey("statusFilters"), JSON.stringify(updated));
    }

    /** Write current checkbox state into page-lifetime memory for this case. */
    function persistCameraSelections(updatedCameras: typeof cameras, knownInnerIds: Set<number> = innerCameraIds) {
        setCameraSelectionMemory(caseId, {
            uncheckedCameraIds: updatedCameras
                .filter(c => !c.isSelected)
                .map(c => c.camera_id),
            checkedOuterCameraIds: updatedCameras
                .filter(c => c.isSelected && !knownInnerIds.has(c.camera_id))
                .map(c => c.camera_id),
        });
    }
    
    function onToggleCameraSelection(camera) {
        const updatedCameras = cameras.map((c) => {
            if (c.camera_id === camera.camera_id) {
                c.isSelected = !c.isSelected;
            }
            return c;
        });

        setCameras(updatedCameras);
        persistCameraSelections(updatedCameras);
    }

    function onCameraMouseOver(camera) {
        const newCameras = cameras.map((c) => {
            if (c.camera_id === camera.camera_id) {
                c.isHighlighted = true;
            }
            return c;
        });

        setCameras(newCameras);
    }

    function onCameraMouseOut(camera) {
        const newCameras = cameras.map((c) => {
            if (c.camera_id === camera.camera_id) {
                c.isHighlighted = false;
            }
            return c;
        });


        setCameras(newCameras);
    }
    
    function onCameraClick(camera) {
        const updatedCameras = cameras.map((c) => {
            if (c.camera_id === camera.camera_id) {
                return { ...c, isSelected: !c.isSelected };
            }
            return c;
        });

        setCameras(updatedCameras);
        persistCameraSelections(updatedCameras);
    }

    function onIncidentUpdated(incident: any) {
        console.log(incident);
        const newFootage = footage.map((f) => {
            if (f.footage_id === incident.footage_id) {
                f = incident;
            }
            return f;
        });
        if (onChange) {
            onChange(newFootage);
        }
        setFootage(newFootage);
    }

    function onToggleSelectAllCameras() {
        const nextValue = !allSelected;

        const updatedCameras = cameras.map(c => ({
            ...c,
            isSelected: nextValue
        }));

        setCameras(updatedCameras);
        persistCameraSelections(updatedCameras);
    }

    const selectCameras = cameras.filter((c) => c.isSelected);
    let statusFilters = [];

    if (showNotReviewed) {
        statusFilters.push(ReviewStatus.PendingEvaluation);
    }
    if (showIncidents) {
        statusFilters.push(ReviewStatus.Incident);
    }

    if (showNoIncidents) {
        statusFilters.push(ReviewStatus.NoIncident);
    }

    const selectedFootage = useMemo(() => footage.filter((f) => selectCameras.some((c) => c.camera_id === f.camera_id)
        && statusFilters.some((s) => f.review_status == s)
    ), [footage, selectCameras, showNotReviewed, showIncidents, showNoIncidents]);

    const allSelected = cameras.length > 0 && cameras.every(c => c.isSelected);

    /** API returns distance in meters; display as miles with two decimals. */
    const formatDistanceMiles = (meters?: number) => {
        if (meters == null || Number.isNaN(Number(meters))) {
            return null;
        }
        return `${(Number(meters) / 1609.344).toFixed(2)} mi`;
    };

    // Sidebar lists: nearest-first within each ring.
    const camerasByDistance = useMemo(
        () => [...cameras].sort((a, b) => (a.distance_meters ?? 0) - (b.distance_meters ?? 0)),
        [cameras]
    );
    const innerCameras = useMemo(
        () => camerasByDistance.filter(c => innerCameraIds.has(c.camera_id)),
        [camerasByDistance, innerCameraIds]
    );
    const outerCameras = useMemo(
        () => camerasByDistance.filter(c => !innerCameraIds.has(c.camera_id)),
        [camerasByDistance, innerCameraIds]
    );

    const formatRadiusLabel = (miles?: number, fallback = 0.25) => {
        const value = Number(miles ?? fallback);
        if (Number.isNaN(value)) {
            return fallback.toFixed(2);
        }
        return value.toFixed(2);
    };
    const innerRadiusLabel = formatRadiusLabel(radiusActive, 0.25);
    const outerRadiusLabel = formatRadiusLabel(radiusMiles, 1);

    const renderCameraCheckbox = (c) => {
        const distanceLabel = formatDistanceMiles(c.distance_meters);
        return (
            <div
                key={c.camera_id}
                className="form-check"
                onMouseOver={() => onCameraMouseOver(c)}
                onMouseLeave={() => onCameraMouseOut(c)}
            >
                <input
                    className="form-check-input"
                    type="checkbox"
                    value="Incident"
                    id={`camera-check-${c.camera_id}`}
                    checked={c.isSelected}
                    onChange={() => onToggleCameraSelection(c)}
                />
                <label className="form-check-label" htmlFor={`camera-check-${c.camera_id}`}>
                    Camera {String(c.camera_id)}
                    {distanceLabel && (
                        <span className="text-muted ms-1">({distanceLabel})</span>
                    )}
                </label>
            </div>
        );
    };

    const round5 = (n: number) => Math.round(n * 1e5) / 1e5;

    // Build offset positions for selected cameras
    const offsetPositions = React.useMemo(() => {
        const offset = 0.00003;
        const selected = (cameras ?? []).filter(c => c.isSelected);

        const groups = new Map<string, typeof selected>();
        for (const c of selected) {
            const key = `${round5(c.lat)},${round5(c.lng)}`;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(c);
        }

        const out = new Map<number, { lat: number; lng: number }>();

        for (const [, group] of groups) {
            if (group.length === 1) {
                const c = group[0];
                out.set(c.camera_id, { lat: c.lat, lng: c.lng });
                continue;
            }

            const used = new Set<string>();
            const keyOf = (a: number, b: number) => `${a.toFixed(7)},${b.toFixed(7)}`;

            for (const c of group) {
                let lat = c.lat;
                let lng = c.lng;
                let tries = 0;

                while (used.has(keyOf(lat, lng)) && tries < 50) {
                    lat += offset;
                    lng += offset;
                    tries++;
                }
                used.add(keyOf(lat, lng));
                out.set(c.camera_id, { lat, lng });
            }
        }

        return out;
    }, [cameras]);

    const disabledPois = React.useMemo(() => {
        return (cameras ?? [])
            .filter(c => !c.isSelected)
            .map(c => {
                const p = offsetPositions.get(c.camera_id) ?? { lat: c.lat, lng: c.lng };
                return {
                    key: `camera-${c.camera_id}`,
                    type: 'camera',
                    location: { lat: p.lat, lng: p.lng },
                    color: c.isHighlighted ? 'blue' : 'red',
                    camera_id: c.camera_id,
                };
            });
    }, [cameras, offsetPositions]);
    
    const pois = React.useMemo(() => {
        return (cameras ?? [])
            .filter(c => c.isSelected)
            .map(c => {
                const p = offsetPositions.get(c.camera_id) ?? { lat: c.lat, lng: c.lng };
                return {
                    key: `camera-${c.camera_id}`,
                    type: 'camera',
                    location: { lat: p.lat, lng: p.lng },
                    color: c.isHighlighted ? 'blue' : 'red',
                    camera_id: c.camera_id,
                };
            });
    }, [cameras, offsetPositions]);

    return (
        <div className="footage-finder">
            <div className="row p-3">


                <div className="filter-panel col-xxl-3 col-xl-4 col-12 mb-3">
                    <div className="by-camera col-12  mb-3">
                        <h2>Filter By Camera</h2>
                        <div className="form-check mb-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="camera-check-all"
                                checked={allSelected}
                                onChange={onToggleSelectAllCameras}
                            />
                            <label className="form-check-label fw-bold" htmlFor="camera-check-all">
                                Select All
                            </label>
                        </div>

                        {/* Inner ring (radiusActive) and outer ring (radiusMiles) — empty groups hidden. */}
                        {innerCameras.length > 0 && (
                            <div className="camera-list-section mb-2">
                                <div className="camera-list-heading text-muted small fw-semibold mb-1">
                                    Within {innerRadiusLabel} mi
                                </div>
                                {innerCameras.map(renderCameraCheckbox)}
                            </div>
                        )}

                        {outerCameras.length > 0 && (
                            <div className="camera-list-section">
                                <div className="camera-list-heading text-muted small fw-semibold mb-1">
                                    Outer ({innerRadiusLabel}–{outerRadiusLabel} mi)
                                </div>
                                {outerCameras.map(renderCameraCheckbox)}
                            </div>
                        )}
                    </div>

                    <div className="by-camera col-12 mb-3">
                        <h2>Filter By Status</h2>

                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                value="Incident"
                                id="not-reviewed"
                                checked={showNotReviewed}
                                onChange={() => {
                                    const next = !showNotReviewed;
                                    setShowNotReviewed(next);
                                    updateStatusFilters({showNotReviewed: next});
                                }}
                            />
                            <label className="form-check-label" htmlFor="not-reviewed">
                                Pending Review
                            </label>
                        </div>

                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                value="Incident"
                                id="incident"
                                checked={showIncidents}
                                onChange={() => {
                                    const next = !showIncidents;
                                    setShowIncidents(next);
                                    updateStatusFilters({showIncidents: next});
                                }}
                            />
                            <label className="form-check-label" htmlFor="incident">
                                Incident
                            </label>
                        </div>

                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                value="Incident"
                                id="no-incident"
                                checked={showNoIncidents}
                                onChange={() => {
                                    const next = !showNoIncidents;
                                    setShowNoIncidents(next);
                                    updateStatusFilters({showNoIncidents: next});
                                }}
                            />
                            <label className="form-check-label" htmlFor="no-incident">
                                No Incident
                            </label>
                        </div>
                    </div>
                    <div className="mini-map col-12 mb-3" style={{height: '300px'}}>
                        <h2>Map</h2>
                        <PreviewMap lat={lat} lng={lng} pois={pois} disabledPois={disabledPois} onPoiClick={(cameraId: number) => {
                            const camera = cameras.find(c => c.camera_id === cameraId);
                            if (camera) onCameraClick(camera);
                        }}/>
                    </div>
                </div>
                <div className="camera-panel col-xxl-9 col-xl-8 col-12">
                    <div className="row">
                        {selectedFootage.map((f) => (
                            <div key={f.footage_id} className="col-xxl-4 col-xl-6 col-12 mb-3" style={{height: '100%'}}>
                                <IncidentViewer footage={f} onIncidentUpdated={onIncidentUpdated}
                                                playbackRate={playbackRate}/>
                            </div>
                        ))}

                        {isLoading && (
                            <div className="d-flex justify-content-center">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        )}

                        {(!isLoading && selectedFootage?.length === 0) && (
                            <div className="no-footage">
                                No footage available for selected cameras
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
